import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SriLankaHeatMap from '../Map/SriLankaHeatMap';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Mock data for news feed
  const newsUpdates = [
    {
      id: 1,
      title: "Flood Warning Issued for Kalutara District",
      date: "10 mins ago",
      type: "alert", 
      summary: "Residents in low-lying areas of Kalutara act advised to evacuate immediately due to rising water levels."
    },
    {
      id: 2,
      title: "Relief Distribution Center Opened in Gampaha",
      date: "2 hours ago",
      type: "info",
      summary: "New distribution center providing dry rations and water bottles is now operational at the Gampaha Public Stadium."
    },
    {
      id: 3,
      title: "Landslide Warning for Ratnapura",
      date: "5 hours ago",
      type: "alert",
      summary: "NBRO issues Level 2 landslide warning for several divisions in Ratnapura district."
    }
  ];

  // Mock data for resources
  const essentialResources = [
    { name: "Emergency Contacts", icon: "call", color: "bg-red-100 text-red-600" },
    { name: "Safe Locations", icon: "verified_user", color: "bg-green-100 text-green-600" },
    { name: "Donation Centers", icon: "volunteer_activism", color: "bg-blue-100 text-blue-600" },
    { name: "Medical Aid", icon: "medical_services", color: "bg-purple-100 text-purple-600" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-gray-800">
      {/* Sidebar Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo Area */}
          <div className="h-20 flex items-center px-6 border-b border-gray-100">
             <img src="/logo.png" alt="Logo" className="h-10 w-10 mr-3 rounded-lg shadow-sm" />
            <h1 className="text-xl font-bold text-gray-900 tracking-tight">Rebuilt<span className="text-blue-600">SL</span></h1>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            <NavItem to="/dashboard" icon="dashboard" label="Dashboard" active />
            <NavItem to="/missing-persons" icon="person_search" label="Missing Persons" />
            <NavItem to="/news" icon="newspaper" label="News & Alerts" />
            <NavItem to="/resources" icon="category" label="Resources" />
            <NavItem to="/create-post" icon="add_location_alt" label="Report Incident" />
            <div className="pt-6 pb-2">
              <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Account</p>
            </div>
            <NavItem to="/profile" icon="person" label="Profile" />
            <NavItem to="/settings" icon="settings" label="Settings" />
          </nav>

          {/* User Bottom Section */}
          <div className="p-4 border-t border-gray-100">
            <div className="flex items-center p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                <span className="material-icons">person</span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">Guest User</p>
                <Link to="/login" className="text-xs text-blue-600 hover:underline">
                  {t('auth.login', 'Login')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm z-10 h-20 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <span className="material-icons">menu</span>
          </button>

          <div className="flex-1 flex justify-end items-center space-x-4">
             <button className="p-2 text-gray-400 hover:text-gray-500 relative">
              <span className="material-icons">notifications</span>
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
            </button>
            <Link
              to="/create-post"
              className="hidden sm:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <span className="material-icons text-sm mr-2">add</span>
              Request Help
            </Link>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-8">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Incidents" value="1,240" icon="warning" color="bg-orange-100 text-orange-600" />
              <StatCard title="People Safe" value="8,932" icon="check_circle" color="bg-green-100 text-green-600" />
              <StatCard title="Missing Persons" value="142" icon="person_off" color="bg-red-100 text-red-600" />
              <StatCard title="Active Relief Ops" value="24" icon="health_and_safety" color="bg-blue-100 text-blue-600" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Heat Map Section - Main Focus */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col h-[500px]">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                  <h3 className="font-bold text-gray-800 flex items-center">
                    <span className="material-icons text-blue-500 mr-2">public</span>
                    {t('dashboard.mapView', 'Live Incident Heat Map')}
                  </h3>
                  <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                    Live Updates
                  </span>
                </div>
                <div className="flex-1 relative">
                  <SriLankaHeatMap />
                </div>
              </div>

              {/* Right Sidebar - News & Quick Links */}
              <div className="space-y-8">
                {/* Quick Resources */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="font-bold text-gray-800 mb-4">Essential Resources</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {essentialResources.map((resource, idx) => (
                       <Link key={idx} to="/resources" className="flex flex-col items-center justify-center p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all duration-200 text-center group">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${resource.color} group-hover:scale-110 transition-transform`}>
                          <span className="material-icons text-xl">{resource.icon}</span>
                        </div>
                        <span className="text-xs font-medium text-gray-600">{resource.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Recent Alerts */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Latest Alerts</h3>
                    <Link to="/news" className="text-sm text-blue-600 hover:text-blue-700 font-medium">View All</Link>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {newsUpdates.map((news) => (
                      <div key={news.id} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start">
                          <span className={`material-icons text-sm mt-1 mr-2 ${
                            news.type === 'alert' ? 'text-red-500' : 'text-blue-500'
                          }`}>
                            {news.type === 'alert' ? 'error' : 'info'}
                          </span>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">{news.title}</h4>
                            <p className="text-xs text-gray-500 mt-1 mb-2">{news.summary}</p>
                            <span className="text-xs text-gray-400">{news.date}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Helper Components
const NavItem: React.FC<{ to: string; icon: string; label: string; active?: boolean }> = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
      active 
        ? 'bg-blue-50 text-blue-700' 
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    <span className={`material-icons mr-3 ${active ? 'text-blue-600' : 'text-gray-400'}`}>{icon}</span>
    {label}
  </Link>
);

const StatCard: React.FC<{ title: string; value: string; icon: string; color: string }> = ({ title, value, icon, color }) => (
  <div className="bg-white overflow-hidden rounded-xl shadow-sm border border-gray-100 p-6 flex items-center">
    <div className={`flex-shrink-0 p-3 rounded-lg ${color}`}>
      <span className="material-icons text-xl">{icon}</span>
    </div>
    <div className="ml-5 w-0 flex-1">
      <dl>
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className="flex items-baseline">
          <div className="text-2xl font-semibold text-gray-900">{value}</div>
        </dd>
      </dl>
    </div>
  </div>
);

export default Dashboard;
